<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import { useLeadsStore } from 'dashboard/stores/leads';
import { useKanbanDnd } from 'dashboard/composables/useKanbanDnd';
import KanbanColumn from './KanbanColumn.vue';
import LeadDrawer from './LeadDrawer.vue';
import MoveToWonDialog from './MoveToWonDialog.vue';
import MoveToLostDialog from './MoveToLostDialog.vue';
import KanbanEmptyState from 'dashboard/components-next/Crm/EmptyState/KanbanEmptyState.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

// Opções fixas para o filtro de classificação no header do Kanban.
const classificationOptions = [
  { value: null, label: 'Todas as classes' },
  { value: 'hot', label: 'Quente' },
  { value: 'warm', label: 'Morno' },
  { value: 'cold', label: 'Frio' },
];

const { t } = useI18n();
const route = useRoute();
const pipelinesStore = usePipelinesStore();
const leadsStore = useLeadsStore();
const {
  state: dndState, onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop,
} = useKanbanDnd();

const pipeline = ref(null);
const leads = ref([]);
const filters = ref({ q: '', classification: null });

// Estado de carregamento/erro do board - alimenta loading e bloco de erro.
const isLoading = ref(false);
const loadError = ref(false);

const drawerRef = ref(null);
const wonDialogRef = ref(null);
const lostDialogRef = ref(null);
const pendingMove = ref(null);

const stages = computed(() => pipeline.value?.stages || []);

const leadsByStage = computed(() => {
  const map = {};
  for (const stage of stages.value) map[stage.id] = [];
  for (const lead of leads.value) {
    if (lead.stage?.id && map[lead.stage.id]) map[lead.stage.id].push(lead);
  }
  return map;
});

const kpis = computed(() => {
  const total = leads.value.length;
  const won = leads.value.filter(l => l.status === 'won');
  const wonAmount = won.reduce((sum, l) => sum + (l.value_cents || 0), 0);
  const avgTicket = won.length ? Math.round(wonAmount / won.length) : 0;
  const stale = leads.value.filter(l => {
    if (l.status !== 'in_pipeline') return false;
    const d = (Date.now() - new Date(l.last_activity_at).getTime()) / 86400000;
    return d > 14;
  }).length;
  const lost = leads.value.filter(l => l.status === 'lost').length;
  const conversion = (won.length + lost) > 0
    ? ((won.length / (won.length + lost)) * 100).toFixed(1)
    : '0.0';
  return { total, wonAmount, avgTicket, stale, conversion };
});

const isEmpty = computed(() => kpis.value.total === 0);

async function load() {
  isLoading.value = true;
  loadError.value = false;
  try {
    pipeline.value = await pipelinesStore.fetchOne(route.params.pipelineId);
    await fetchLeads();
    maybeOpenDeepLink();
  } catch (error) {
    loadError.value = true;
  } finally {
    isLoading.value = false;
  }
}

function maybeOpenDeepLink() {
  const leadId = route.query?.lead;
  if (!leadId) return;
  nextTick(() => drawerRef.value?.open(Number(leadId)));
}

// Trocar de pipeline no menu muda só `route.params.pipelineId` - o Vue Router
// reusa o componente e `onMounted` NÃO dispara de novo. Sem este watch a tela
// fica com os dados do pipeline anterior. `immediate` cobre o load inicial.
watch(
  () => route.params.pipelineId,
  id => {
    if (id) load();
  },
  { immediate: true }
);

watch(
  () => route.query.lead,
  newId => {
    if (newId) drawerRef.value?.open(Number(newId));
  }
);

// SelectInput não emite @change - refazer fetch ao mudar o filtro.
watch(() => filters.value.classification, () => { fetchLeads(); });

async function fetchLeads() {
  const result = await leadsStore.fetch({
    pipeline_id: route.params.pipelineId,
    per_page: 100,
    ...filters.value,
  });
  leads.value = result.leads || [];
}

async function handleMove({ leadId, toStageId }) {
  const stage = stages.value.find(s => s.id === toStageId);
  if (!stage) return;

  if (stage.kind === 'won') {
    pendingMove.value = { leadId, stage };
    wonDialogRef.value?.open();
    return;
  }
  if (stage.kind === 'lost') {
    pendingMove.value = { leadId, stage };
    lostDialogRef.value?.open();
    return;
  }
  await leadsStore.moveStage(leadId, toStageId, null);
  await fetchLeads();
}

async function confirmWon(payload) {
  if (!pendingMove.value) return;
  await leadsStore.win(pendingMove.value.leadId, payload);
  pendingMove.value = null;
  await fetchLeads();
}

async function confirmLost(payload) {
  if (!pendingMove.value) return;
  await leadsStore.lose(pendingMove.value.leadId, payload);
  pendingMove.value = null;
  await fetchLeads();
}

function openLead(lead) {
  drawerRef.value?.open(lead.id);
}

function formatMoney(cents) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
    .format((Number(cents) || 0) / 100);
}

</script>

<template>
  <!-- Estado de erro: falha ao carregar o pipeline/leads. Bloco premium
       centralizado com ícone, título/subtítulo (i18n) e ação de retry. -->
  <div
    v-if="loadError"
    class="w-full h-full flex items-center justify-center bg-n-surface-1 font-inter px-6"
  >
    <div
      class="flex flex-col items-center text-center max-w-sm rounded-xl border border-n-weak bg-n-solid-1 px-8 py-10"
    >
      <span
        class="flex items-center justify-center size-12 rounded-full bg-n-ruby-3 mb-4"
      >
        <i class="i-lucide-cloud-off size-6 text-n-ruby-11" />
      </span>
      <h2 class="text-heading-2 text-n-slate-12 mb-1">
        {{ t('PIPELINES.KANBAN.ERROR.TITLE') }}
      </h2>
      <p class="text-body-main text-n-slate-11 mb-5">
        {{ t('PIPELINES.KANBAN.ERROR.DESCRIPTION') }}
      </p>
      <button
        type="button"
        class="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-n-brand text-white text-body-main font-medium hover:bg-n-brand/90 transition-colors"
        @click="load"
      >
        <i class="i-lucide-rotate-cw size-4" />
        {{ t('PIPELINES.KANBAN.ERROR.RETRY') }}
      </button>
    </div>
  </div>

  <!-- Wrapper segue o padrão de Relatórios → Visão geral:
       bg surface, scroll vertical no container, padding lateral consistente
       e largura máxima para a área de conteúdo. -->
  <div v-else-if="pipeline" class="w-full h-full overflow-y-auto bg-n-surface-1 font-inter">
    <div class="max-w-[88rem] mx-auto px-6 py-6 flex flex-col gap-4">

      <!-- Header padrão da plataforma -->
      <header class="flex items-center justify-between gap-4">
        <div class="min-w-0">
          <div class="flex items-center gap-2.5">
            <span class="size-3 rounded-full shrink-0" :style="{ background: pipeline.color }" />
            <h1 class="text-heading-1 text-n-slate-12 min-h-10 flex items-center mb-0 truncate">
              {{ pipeline.name }}
            </h1>
          </div>
          <p v-if="pipeline.description" class="text-body-main text-n-slate-11 mb-0 mt-1 max-w-3xl">
            {{ pipeline.description }}
          </p>
        </div>
      </header>

      <!-- Card de KPIs (padrão "CRM em números") -->
      <section class="rounded-xl border border-n-weak bg-n-solid-1 px-6 py-5">
        <header class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-2">
            <i class="i-lucide-target size-4 text-n-brand" />
            <h2 class="text-heading-3 text-n-slate-12 mb-0">Visão do pipeline</h2>
          </div>
        </header>

        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div class="rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3">
            <div class="flex items-center gap-1.5 mb-1.5">
              <i class="i-lucide-users size-3.5 text-n-slate-10" />
              <p class="text-label-small text-n-slate-11 uppercase tracking-wide mb-0">Total</p>
            </div>
            <p class="text-heading-2 text-n-slate-12 mb-0">{{ kpis.total }}</p>
            <p class="text-body-small text-n-slate-10 mb-0">leads no pipeline</p>
          </div>
          <div class="rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3">
            <div class="flex items-center gap-1.5 mb-1.5">
              <i class="i-lucide-trophy size-3.5 text-n-grass-11" />
              <p class="text-label-small text-n-slate-11 uppercase tracking-wide mb-0">Ganho</p>
            </div>
            <p class="text-heading-2 text-n-grass-11 mb-0">{{ formatMoney(kpis.wonAmount) }}</p>
            <p class="text-body-small text-n-slate-10 mb-0">soma dos negócios fechados</p>
          </div>
          <div class="rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3">
            <div class="flex items-center gap-1.5 mb-1.5">
              <i class="i-lucide-receipt size-3.5 text-n-slate-10" />
              <p class="text-label-small text-n-slate-11 uppercase tracking-wide mb-0">Ticket médio</p>
            </div>
            <p class="text-heading-2 text-n-slate-12 mb-0">{{ formatMoney(kpis.avgTicket) }}</p>
            <p class="text-body-small text-n-slate-10 mb-0">sobre os ganhos</p>
          </div>
          <div class="rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3">
            <div class="flex items-center gap-1.5 mb-1.5">
              <i
                class="i-lucide-alert-triangle size-3.5"
                :class="kpis.stale > 0 ? 'text-n-amber-11' : 'text-n-slate-10'"
              />
              <p class="text-label-small text-n-slate-11 uppercase tracking-wide mb-0">Parados</p>
            </div>
            <p class="text-heading-2 mb-0" :class="kpis.stale > 0 ? 'text-n-amber-11' : 'text-n-slate-12'">
              {{ kpis.stale }}
            </p>
            <p class="text-body-small text-n-slate-10 mb-0">sem ação há mais de 14 dias</p>
          </div>
        </div>
      </section>

      <!-- Card do Kanban: filtros no header + corpo com colunas (ou empty state) -->
      <section class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden">
        <header class="px-6 py-4 border-b border-n-weak flex items-center justify-between gap-3 flex-wrap">
          <div class="flex items-center gap-2 min-w-0">
            <i class="i-lucide-kanban size-4 text-n-slate-11" />
            <h2 class="text-heading-3 text-n-slate-12 mb-0">Funil</h2>
            <span class="text-body-small text-n-slate-11">· {{ stages.length }} estágios</span>
          </div>

          <div class="flex items-center gap-2">
            <div class="relative w-56">
              <Input
                :model-value="filters.q"
                type="search"
                placeholder="Buscar contato..."
                :custom-input-class="[
                  'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
                ]"
                class="w-full"
                @input="filters.q = $event.target.value; fetchLeads()"
              >
                <template #prefix>
                  <Icon
                    icon="i-lucide-search"
                    class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
                  />
                </template>
              </Input>
            </div>
            <div class="w-44">
              <SelectInput
                v-model="filters.classification"
                :options="classificationOptions"
              />
            </div>
          </div>
        </header>

        <!-- Quando não há leads: empty state oficial (EmptyStateLayout) com
             cards-fantasma de stages + sample de leads, igual ao padrão dos demais
             empty states do CRM (Pipelines, FollowUpTemplates, MyTasks). -->
        <div v-if="isEmpty" class="h-[32rem] min-h-[32rem]">
          <KanbanEmptyState />
        </div>

        <!-- Kanban real - colunas roláveis horizontalmente. -->
        <div
          v-else
          class="overflow-x-auto p-4 flex gap-3 items-start min-h-[32rem]"
        >
          <KanbanColumn
            v-for="(stage, stageIdx) in stages"
            :key="stage.id"
            :stage="stage"
            :stage-index="stageIdx"
            :stage-count="stages.length"
            :leads="leadsByStage[stage.id] || []"
            :is-drag-over="dndState.dragOverStageId === stage.id"
            @dragover="onDragOver($event, stage.id)"
            @dragleave="onDragLeave($event, stage.id)"
            @drop="onDrop($event, stage.id, handleMove)"
            @leadDragStart="({ lead, event }) => onDragStart(event, lead, stage.id)"
            @leadDragEnd="onDragEnd"
            @leadClick="openLead"
          />
        </div>
      </section>
    </div>

    <LeadDrawer ref="drawerRef" @lead-updated="fetchLeads" />
    <MoveToWonDialog ref="wonDialogRef" @confirm="confirmWon" />
    <MoveToLostDialog ref="lostDialogRef" :pipeline-id="pipeline?.id" @confirm="confirmLost" />
  </div>

  <!-- Loading inicial padrão da plataforma enquanto o pipeline carrega -->
  <div v-else class="w-full h-full flex items-center justify-center bg-n-surface-1">
    <Spinner />
  </div>
</template>
